/**
 * دکمه‌های «ورود با …» — یک فهرست، برای هر دو اپ.
 *
 * ═══ چرا این فایل هست ═══
 *
 * تا قبل از این، هر اپ خودش تصمیم می‌گرفت کدام دکمه را نشان بدهد: موبایل
 * گوگل و فیس‌بوک را می‌شناخت (دومی پشتِ یک کلیدِ خاموش)، و وب فقط گوگل
 * داشت. دو فهرستِ جدا که هیچ‌چیز هم‌سطحشان نگه نمی‌داشت.
 *
 * حالا که «ورود با Apple» اضافه شده این دیگر فقط بی‌نظمی نیست، یک قاعده‌ی
 * فروشگاهی‌ست:
 *
 *   اپل اجازه نمی‌دهد اپی که ورودِ شخصِ ثالث دارد، «ورود با Apple» نداشته
 *   باشد — و آن دکمه باید **دستِ‌کم به‌اندازه‌ی بقیه دیده شود**.
 *
 * «دستِ‌کم به‌اندازه‌ی بقیه» چیزی نیست که بشود در دو فایلِ جدا نگه داشت:
 * کافی‌ست یک روز کسی در یکی از دو اپ دکمه‌ای بالاتر اضافه کند و هیچ تستی
 * صدایش درنیاید. پس ترتیب همین‌جا تثبیت شده و هر دو اپ روی همین آرایه
 * `map` می‌زنند. جابه‌جا کردنِ Apple از صدر، `authProviders.test.ts` را
 * می‌شکند.
 */

/** ارائه‌دهنده‌هایی که Supabase برایشان تنظیم شده یا خواهد شد. */
export type OAuthProvider = 'apple' | 'google' | 'facebook';

export interface OAuthButton {
  provider: OAuthProvider;
  /** کلیدِ i18n. متنش در هر ۱۳ زبان هست. */
  labelKey: 'continueApple' | 'continueGoogle' | 'continueFacebook';
  /**
   * آیا الان نشان داده شود.
   *
   * فیس‌بوک `false` است چون کلیدهایش هنوز روی Supabase ثبت نشده‌اند و زدنِ
   * دکمه فقط «این راه هنوز باز نشده» می‌دهد — که از نبودنِ دکمه بدتر است.
   * این خاموش/روشن است نه حذف: `signInWithOAuth` هنوز می‌پذیردش، متنِ
   * دکمه در همه‌ی زبان‌ها سرِ جایش است، و `scripts/oauth-config.mjs` هنوز
   * کلیدهایش را تنظیم می‌کند.
   */
  enabled: boolean;
}

/**
 * ترتیب **معنادار** است: Apple اول.
 *
 * نه از سرِ سلیقه — بندِ ۴.۸ راهنمای بازبینیِ اپل. پایین‌تر بردنش یعنی
 * ریسکِ رد شدن، و آن چیزی نیست که در یک دیفِ رابط‌کاربری دیده شود.
 */
export const OAUTH_BUTTONS: readonly OAuthButton[] = [
  { provider: 'apple', labelKey: 'continueApple', enabled: true },
  { provider: 'google', labelKey: 'continueGoogle', enabled: true },
  { provider: 'facebook', labelKey: 'continueFacebook', enabled: false },
];

/** همان فهرست، فقط آن‌هایی که واقعاً روی صفحه می‌نشینند. */
export const visibleOAuthButtons = (): readonly OAuthButton[] =>
  OAUTH_BUTTONS.filter((b) => b.enabled);

/**
 * فهرستِ اپِ موبایل: فقط دکمه‌هایی که روی همین دستگاه راهِ **نیتیو** دارند
 * (WHOSUP-306).
 *
 * بازبینیِ اپل ایراد گرفت که ورود کاربر را به Safari می‌فرستد، و تصمیم این
 * شد که روی گوشی هیچ مرورگری — نه بیرونی، نه برگه‌ی درون‌اپیِ Safari — برای
 * ورود باز نشود. پس دکمه‌ای که راهی جز مرورگر ندارد (Apple روی اندروید،
 * فیس‌بوک، گوگلی که شناسه‌ی کلاینتش در بیلد نیامده) اصلاً نشان داده نمی‌شود؛
 * دکمه‌ای که بزنی و مرورگر باز کند همان ایرادِ رد شدن است.
 *
 * ترتیب همان `OAUTH_BUTTONS` است، پس Apple هر جا باشد اول است.
 */
export const nativeOAuthButtons = (
  hasNative: (p: OAuthProvider) => boolean,
): readonly OAuthButton[] => visibleOAuthButtons().filter((b) => hasNative(b.provider));
